/**
 * RegionalPricingService - Hierarchical Regional Pricing Engine
 * 
 * Implements hierarchical pricing system with intelligent fallback:
 * Country Override → Regional → Continental → Global Default
 * 
 * Features:
 * - Smart caching with 1-hour TTL
 * - Country detection integration
 * - Currency conversion support
 * - Performance optimized queries
 * - Comprehensive error handling
 */

import { supabase } from '@/integrations/supabase/client';
import { lambdaDataService } from '@/services/LambdaDataService';
import { currencyService } from '@/services/CurrencyService';
import { logger } from '@/utils/logger';
import { performanceMonitoringService } from '@/services/PerformanceMonitoringService';
import { convertCurrency, formatAmountWithFinancialPrecision } from '@/utils/quoteCurrencyUtils';

export interface AddonService {
  id: string;
  service_key: string;
  service_name: string;
  service_description?: string;
  service_category: 'protection' | 'processing' | 'support' | 'extras';
  pricing_type: 'percentage' | 'fixed' | 'tiered';
  default_rate: number;
  min_amount?: number;
  max_amount?: number;
  is_active: boolean;
  is_default_enabled: boolean;
  requires_order_value: boolean;
  supported_order_types: string[];
  display_order: number;
  icon_name?: string;
  badge_text?: string;
  business_rules?: Record<string, any>;
}

export interface PricingCalculation {
  service_key: string;
  service_name: string;
  pricing_type: 'percentage' | 'fixed' | 'tiered';
  
  // Rate information
  applicable_rate: number;
  calculated_amount: number;
  min_amount: number;
  max_amount?: number;
  
  // Source tracking
  pricing_tier: 'global' | 'continental' | 'regional' | 'country';
  source_description: string;
  
  // Metadata
  currency_code: string;
  order_value?: number;
  is_cached: boolean;
  calculation_timestamp: string;
}

export interface PricingRequest {
  service_keys: string[];
  country_code: string;
  order_value?: number;
  currency_code?: string;
  use_cache?: boolean;
}

export interface PricingResponse {
  success: boolean;
  calculations: PricingCalculation[];
  total_addon_cost: number;
  currency_code: string;
  country_code: string;
  error?: string;
  cache_hit_rate?: number;
}

export interface CountryPricingInfo {
  country_code: string;
  continent?: string;
  supported_services: string[];
  regional_groups: string[];
  has_country_overrides: boolean;
}

class RegionalPricingServiceClass {
  private static instance: RegionalPricingServiceClass;
  private cache = new Map<string, { data: any; expires: number }>();
  private logThrottle = new Map<string, number>();
  private readonly CACHE_DURATION = 60 * 60 * 1000; // 1 hour
  private globalConfig: {
    default_rate: number;
    min_amount: number;
    max_amount: number;
  } | null = null;
  
  private constructor() {}

  /**
   * Load global pricing configuration from system_settings
   */
  private async loadGlobalConfig(): Promise<void> {
    if (this.globalConfig) return; // Already loaded

    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('setting_key, setting_value')
        .in('setting_key', [
          'global_default_pricing_rate',
          'global_pricing_min_amount', 
          'global_pricing_max_amount'
        ]);

      if (error) throw error;

      if (data) {
        const configMap = data.reduce((acc, setting) => {
          acc[setting.setting_key] = setting.setting_value;
          return acc;
        }, {} as Record<string, string>);

        this.globalConfig = {
          default_rate: parseFloat(configMap.global_default_pricing_rate || '0.025'),
          min_amount: parseFloat(configMap.global_pricing_min_amount || '2.00'),
          max_amount: parseFloat(configMap.global_pricing_max_amount || '250.00')
        };

        logger.debug('Global pricing configuration loaded', { config: this.globalConfig });
      } else {
        // Fallback to hardcoded values if no config found
        this.globalConfig = {
          default_rate: 0.025,
          min_amount: 2.00,
          max_amount: 250.00
        };
        logger.debug('No global pricing config found, using defaults', { config: this.globalConfig });
      }
    } catch (error) {
      logger.error('Failed to load global pricing config', { error });
      // Fallback to hardcoded values
      this.globalConfig = {
        default_rate: 0.025,
        min_amount: 2.00,
        max_amount: 250.00
      };
    }
  }
  
  public static getInstance(): RegionalPricingServiceClass {
    if (!RegionalPricingServiceClass.instance) {
      RegionalPricingServiceClass.instance = new RegionalPricingServiceClass();
    }
    return RegionalPricingServiceClass.instance;
  }

  /**
   * Get available addon services with their base configurations
   */
  async getAvailableServices(category?: string): Promise<AddonService[]> {
    const startTime = Date.now();
    const operationId = `get_services_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const cacheKey = `services_${category || 'all'}`;
    
    // Start performance monitoring
    performanceMonitoringService.startOperation(operationId, 'get_available_services', {
      category: category || 'all'
    });

    try {
      // Check cache first
      const cached = this.getFromCache(cacheKey);
      if (cached) {
        // End performance monitoring - cache hit
        performanceMonitoringService.endOperation(
          operationId,
          'get_available_services',
          startTime,
          true,
          true,
          undefined,
          category,
          undefined,
          undefined,
          { serviceCount: cached.length }
        );
        return cached;
      }

      let query = supabase
        .from('addon_services')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (category) {
        query = query.eq('service_category', category);
      }

      const { data, error } = await query;
      
      if (error) {
        logger.error('Failed to fetch addon services:', error);
        
        // End performance monitoring - error
        performanceMonitoringService.endOperation(
          operationId,
          'get_available_services',
          startTime,
          false,
          false,
          undefined,
          category,
          undefined,
          `Database error: ${error.message}`,
          { category }
        );
        
        throw new Error(`Database error: ${error.message}`);
      }

      const services = data || [];
      this.setCache(cacheKey, services);
      
      // End performance monitoring - success
      performanceMonitoringService.endOperation(
        operationId,
        'get_available_services',
        startTime,
        true,
        false,
        undefined,
        category,
        undefined,
        undefined,
        { serviceCount: services.length }
      );
      
      logger.debug(`[RegionalPricing] Loaded ${services.length} addon services`);
      return services;
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      
      // End performance monitoring - error
      performanceMonitoringService.endOperation(
        operationId,
        'get_available_services',
        startTime,
        false,
        false,
        undefined,
        category,
        undefined,
        errorMessage,
        { category }
      );
      
      logger.error('Error fetching addon services:', error);
      throw error;
    }
  }

  /**
   * Simple helper to ensure consistent country-currency mapping
   */
  private getCorrectCountryFromCurrency(currency_code: string, fallback_country: string): string {
    // Simple mapping without external dependency to avoid import issues
    const currencyToCountry: Record<string, string> = {
      'NPR': 'NP',  // Nepal
      'INR': 'IN',  // India  
      'USD': 'US',  // United States
      'EUR': 'DE',  // Germany (primary for EUR)
      'GBP': 'GB',  // United Kingdom
      'JPY': 'JP',  // Japan
      'CNY': 'CN',  // China
      'AUD': 'AU',  // Australia
      'CAD': 'CA',  // Canada
    };
    
    const derivedCountry = currencyToCountry[currency_code?.toUpperCase()] || fallback_country || 'US';
    return derivedCountry;
  }

  /**
   * Calculate pricing for multiple services and a specific country
   */
  async calculatePricing(request: PricingRequest): Promise<PricingResponse> {
    // 🔥 SIMPLE FIX: Force correct country from currency to prevent inconsistency
    const correctedCountry = this.getCorrectCountryFromCurrency(request.currency_code, request.country_code);
    const correctedRequest = { ...request, country_code: correctedCountry };
    
    // Early return for empty service lists to avoid unnecessary processing
    if (!correctedRequest.service_keys || correctedRequest.service_keys.length === 0) {
      return {
        success: true,
        calculations: [],
        total_addon_cost: 0,
        currency_code: correctedRequest.currency_code || 'USD',
        country_code: correctedCountry?.toUpperCase() || 'US',
        cache_hit_rate: 100
      };
    }

    const startTime = Date.now();
    const operationId = `pricing_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    let cacheHits = 0;

    // Load global config if not already loaded
    await this.loadGlobalConfig();
    let totalQueries = 0;

    // Start performance monitoring
    performanceMonitoringService.startOperation(operationId, 'calculate_pricing', {
      countryCode: correctedCountry,
      serviceKeys: correctedRequest.service_keys,
      orderValue: correctedRequest.order_value
    });

    try {
      const currency = correctedRequest.currency_code || 'USD';
      const serviceKeys = correctedRequest.service_keys || [];

      // Process each service - OPTIMIZED with parallel processing
      const calculationPromises = serviceKeys.map(async (service_key) => {
        totalQueries++;
        
        try {
          const calculation = await this.calculateServicePricing(
            service_key,
            correctedCountry,
            correctedRequest.order_value,
            currency,
            request.use_cache !== false
          );
          
          if (calculation.is_cached) {
            cacheHits++;
          }
          
          return calculation;
        } catch (error) {
          logger.error(`Service ${service_key} pricing calculation failed:`, error);
          return null; // Return null for failed services
        }
      });
      
      // Wait for all calculations to complete in parallel
      const calculationResults = await Promise.all(calculationPromises);
      
      // Filter out failed calculations (null values)
      const calculations = calculationResults.filter(calc => calc !== null);

      // Calculate total addon cost
      const total_addon_cost = calculations.reduce((sum, calc) => sum + calc.calculated_amount, 0);

      const response: PricingResponse = {
        success: true,
        calculations,
        total_addon_cost,
        currency_code: currency,
        country_code: correctedCountry,
        cache_hit_rate: totalQueries > 0 ? (cacheHits / totalQueries) * 100 : 0
      };

      const duration = Date.now() - startTime;
      const overallCacheHit = cacheHits > 0;

      // End performance monitoring - success
      performanceMonitoringService.endOperation(
        operationId,
        'calculate_pricing',
        startTime,
        true,
        overallCacheHit,
        correctedCountry,
        correctedRequest.service_keys?.join(','),
        correctedRequest.order_value,
        undefined,
        {
          totalServices: correctedRequest.service_keys?.length || 0,
          cacheHitRate: response.cache_hit_rate,
          totalCost: total_addon_cost
        }
      );

      logger.debug(`[RegionalPricing] Pricing calculated in ${duration}ms, cache hit rate: ${response.cache_hit_rate?.toFixed(1)}%`);
      return response;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown pricing error';
      
      // End performance monitoring - error
      performanceMonitoringService.endOperation(
        operationId,
        'calculate_pricing',
        startTime,
        false,
        false,
        correctedCountry,
        correctedRequest.service_keys?.join(','),
        correctedRequest.order_value,
        errorMessage,
        {
          totalServices: correctedRequest.service_keys?.length || 0
        }
      );

      logger.error('Error calculating pricing:', error);
      return {
        success: false,
        calculations: [],
        total_addon_cost: 0,
        currency_code: request.currency_code || 'USD',
        country_code: request.country_code,
        error: errorMessage
      };
    }
  }

  /**
   * Calculate pricing for a single service using hierarchical fallback
   */
  private async calculateServicePricing(
    service_key: string,
    country_code: string,
    order_value?: number,
    currency_code: string = 'USD',
    use_cache: boolean = true
  ): Promise<PricingCalculation> {
    
    const cacheKey = `pricing_${service_key}_${country_code}_${order_value || 0}_${currency_code}`;
    
    // Check cache first
    if (process.env.NODE_ENV === 'development' && service_key === 'package_protection') {
      
    }
    
    try {
      // Check cache first
      if (use_cache) {
        const cached = this.getFromCache(cacheKey);
        if (cached) {
          if (process.env.NODE_ENV === 'development') {
            console.log(`💾 [add-on-pricing] Cache hit for ${service_key}:`, cached);
          }
          return { ...cached, is_cached: true };
        }
      }

      // Get service configuration
      const service = await this.getServiceConfig(service_key);
      if (!service) {
        throw new Error(`Service not found: ${service_key}`);
      }

      if (process.env.NODE_ENV === 'development') {
        console.log(`📋 [add-on-pricing] Service config for ${service_key}:`, {
          id: service.id,
          pricing_type: service.pricing_type,
          default_rate: service.default_rate,
          min_amount: service.min_amount,
          max_amount: service.max_amount
        });
      }

      // Try hierarchical pricing lookup: Country → Regional → Continental → Global
      const pricingRule = await this.findApplicablePricingRule(service.id, country_code);
      
      if (process.env.NODE_ENV === 'development') {
        
      }
      
      // Calculate the final amount
      const calculatedAmount = await this.calculateFinalAmount(
        service,
        pricingRule,
        order_value,
        currency_code
      );

      if (process.env.NODE_ENV === 'development') {
        console.log(`💰 [add-on-pricing] Final calculation for ${service_key}:`, {
          rawAmount: calculatedAmount.amount,
          min_amount: calculatedAmount.min_amount,
          max_amount: calculatedAmount.max_amount,
          order_value,
          rate: pricingRule.rate,
          pricing_type: service.pricing_type,
          currency_code
        });
      }

      const calculation: PricingCalculation = {
        service_key,
        service_name: service.service_name,
        pricing_type: service.pricing_type,
        applicable_rate: pricingRule.rate,
        calculated_amount: calculatedAmount.amount,
        min_amount: calculatedAmount.min_amount,
        max_amount: calculatedAmount.max_amount,
        pricing_tier: pricingRule.tier,
        source_description: pricingRule.description,
        currency_code,
        order_value,
        is_cached: false,
        calculation_timestamp: new Date().toISOString()
      };

      if (process.env.NODE_ENV === 'development') {
        console.log(`✅ [add-on-pricing] Final calculation result for ${service_key}:`, calculation);
      }

      // Cache the result
      if (use_cache) {
        this.setCache(cacheKey, calculation);
      }

      // Also cache in database for longer-term storage
      if (order_value) {
        this.cacheInDatabase(service.id, country_code, order_value, pricingRule, calculatedAmount.amount).catch(err => {
          logger.debug('Failed to cache pricing in database:', err);
        });
      }

      return calculation;

    } catch (error) {
      logger.error(`Error calculating pricing for ${service_key}:`, error);
      if (process.env.NODE_ENV === 'development') {
        console.error(`❌ [add-on-pricing] Error for ${service_key}:`, error);
      }
      throw error;
    }
  }

  /**
   * Find the most specific applicable pricing rule using hierarchical fallback
   */
  private async findApplicablePricingRule(service_id: string, country_code: string) {
    try {
      if (process.env.NODE_ENV === 'development') {
        
      }

      // 1. Try country-specific override first (highest priority)
      const countryRule = await this.getCountryOverride(service_id, country_code);
      if (countryRule) {
        if (process.env.NODE_ENV === 'development') {
          
        }
        return {
          rate: countryRule.rate,
          min_amount: countryRule.min_amount || 0,
          max_amount: countryRule.max_amount,
          tier: 'country' as const,
          description: `Country-specific rate for ${country_code}: ${countryRule.reason || 'Special pricing'}`
        };
      }

      if (process.env.NODE_ENV === 'development') {
        
      }

      // 2. Try regional pricing (second priority)
      const regionalRule = await this.getRegionalPricing(service_id, country_code);
      if (regionalRule) {
        if (process.env.NODE_ENV === 'development') {
          
        }
        return {
          rate: regionalRule.rate,
          min_amount: regionalRule.min_amount || 0,
          max_amount: regionalRule.max_amount,
          tier: 'regional' as const,
          description: `Regional rate (${regionalRule.region_name}): ${regionalRule.notes || 'Regional pricing'}`
        };
      }

      if (process.env.NODE_ENV === 'development') {
        
      }

      // 3. Try continental pricing (third priority)
      const continentalRule = await this.getContinentalPricing(service_id, country_code);
      if (continentalRule) {
        if (process.env.NODE_ENV === 'development') {
          
        }
        return {
          rate: continentalRule.rate,
          min_amount: continentalRule.min_amount || 0,
          max_amount: continentalRule.max_amount,
          tier: 'continental' as const,
          description: `Continental rate (${continentalRule.continent}): ${continentalRule.notes || 'Continental pricing'}`
        };
      }

      if (process.env.NODE_ENV === 'development') {
        
      }

      // 4. Fall back to global default (lowest priority)
      const service = await this.getServiceConfig(null, service_id);
      if (!service) {
        throw new Error('Service configuration not found for fallback');
      }

      // Use configurable global rate if available, otherwise fall back to service default
      const globalRate = this.globalConfig?.default_rate || service.default_rate;
      const globalMinAmount = this.globalConfig?.min_amount || service.min_amount || 0;
      const globalMaxAmount = this.globalConfig?.max_amount || service.max_amount;

      if (process.env.NODE_ENV === 'development') {
        
      }

      return {
        rate: globalRate,
        min_amount: globalMinAmount,
        max_amount: globalMaxAmount,
        tier: 'global' as const,
        description: 'Global default rate - no specific regional pricing available'
      };

    } catch (error) {
      logger.error('Error finding pricing rule:', error);
      if (process.env.NODE_ENV === 'development') {
        console.error(`❌ [add-on-pricing] Error finding pricing rule:`, error);
      }
      throw error;
    }
  }

  /**
   * Get country-specific pricing override
   */
  private async getCountryOverride(service_id: string, country_code: string) {
    // Skip if country_code is invalid
    if (!this.isValidCountryCode(country_code)) {
      return null;
    }

    const currentDate = new Date().toISOString();
    const { data, error } = await supabase
      .from('country_pricing_overrides')
      .select('*')
      .eq('service_id', service_id)
      .eq('country_code', country_code)
      .eq('is_active', true)
      .lte('effective_from', currentDate)
      .or(`effective_until.is.null,effective_until.gt.${currentDate}`)
      .maybeSingle();

    if (error && error.code !== 'PGRST116') { // PGRST116 = no rows found
      logger.error('Error fetching country override:', error);
    }

    return data;
  }

  /**
   * Get regional pricing (countries grouped in custom regions)
   */
  private async getRegionalPricing(service_id: string, country_code: string) {
    // Skip if country_code is invalid
    if (!this.isValidCountryCode(country_code)) {
      return null;
    }

    const { data, error } = await supabase
      .from('regional_pricing')
      .select('*')
      .eq('service_id', service_id)
      .contains('country_codes', [country_code])
      .eq('is_active', true)
      .order('priority', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error && error.code !== 'PGRST116') {
      logger.error('Error fetching regional pricing:', error);
    }

    return data;
  }

  /**
   * Get continental pricing using country_settings table
   */
  /**
   * Validate country code format
   */
  private isValidCountryCode(country_code: string): boolean {
    const result = !!(country_code && 
              country_code.length === 2 && 
              country_code !== 'GLOBAL' &&
              /^[A-Z]{2}$/.test(country_code));
    
    console.log(`🚨 [country-debug] isValidCountryCode('${country_code}'):`, {
      input: country_code,
      exists: !!country_code,
      length: country_code?.length,
      length_check: country_code?.length === 2,
      not_global: country_code !== 'GLOBAL',
      regex_test: /^[A-Z]{2}$/.test(country_code),
      final_result: result
    });
    
    return result;
  }

  private async getContinentalPricing(service_id: string, country_code: string) {
    // Skip if country_code is invalid
    if (!this.isValidCountryCode(country_code)) {
      return null;
    }

    // First get the continent for this country via Lambda API
    const response = await lambdaDataService.getCountries();
    if (!response.success) {
      logger.debug(`Failed to fetch countries data for ${country_code}`);
      return null;
    }

    const countryInfo = response.data.find(c => c.code === country_code);
    if (!countryInfo) {
      logger.debug(`Country ${country_code} not found in countries data`);
      return null;
    }

    if (!countryInfo?.continent) {
      return null;
    }

    // Then get continental pricing for this continent
    const { data, error } = await supabase
      .from('continental_pricing')
      .select('*')
      .eq('service_id', service_id)
      .eq('continent', countryInfo.continent)
      .eq('is_active', true)
      .maybeSingle();

    if (error && error.code !== 'PGRST116') {
      logger.error('Error fetching continental pricing:', error);
    }

    return data ? { ...data, continent: countryInfo.continent } : null;
  }

  /**
   * Calculate final amount with min/max constraints and currency conversion
   */
  private async calculateFinalAmount(
    service: AddonService,
    pricingRule: any,
    order_value?: number,
    currency_code: string = 'USD'
  ) {
    if (process.env.NODE_ENV === 'development') {
      console.log(`💰 [add-on-pricing] Starting calculation:`, {
        service_key: service.service_key,
        pricing_type: service.pricing_type,
        pricing_rule_rate: pricingRule.rate,
        order_value,
        currency_code
      });
    }

    let amount = 0;
    const min_amount = pricingRule.min_amount || service.min_amount || 0;
    const max_amount = pricingRule.max_amount || service.max_amount;

    if (service.pricing_type === 'percentage') {
      
      console.log(`[convert-user] Service: ${service.service_key}`, {
        order_value,
        currency_code,
        pricing_rule_rate: pricingRule.rate,
        expected_calculation: order_value ? `${order_value} × ${pricingRule.rate} = ${order_value * pricingRule.rate}` : 'No order value',
        is_order_value_valid: !!order_value
      });
      
      if (!order_value) {
        // If no order value provided but service requires it, use minimum
        amount = min_amount;
        console.log(`[convert-user] ⚠️ No valid order value, using minimum: ${min_amount}`);
        if (process.env.NODE_ENV === 'development') {
          console.log(`💰 [add-on-pricing] No order value, using minimum: ${min_amount}`);
        }
      } else {
        // 🔥 CRITICAL FIX: For percentage services, rates are designed for USD base values
        // If we have a non-USD currency, we need to calculate percentage on USD equivalent
        if (currency_code !== 'USD') {
          
          try {
            // Convert order value back to USD for percentage calculation
            const orderValueUSD = await convertCurrency(order_value, currency_code, 'USD');
            console.log(`[convert-user] Order value conversion: ${order_value} ${currency_code} → ${orderValueUSD} USD`);
            
            // Apply percentage to USD value
            const amountUSD = orderValueUSD * pricingRule.rate;
            console.log(`[convert-user] USD percentage calc: ${orderValueUSD} × ${pricingRule.rate} = ${amountUSD} USD`);
            
            // Convert result back to target currency
            amount = await convertCurrency(amountUSD, 'USD', currency_code);
            console.log(`[convert-user] Final conversion: ${amountUSD} USD → ${amount} ${currency_code}`);
          } catch (error) {
            console.error(`[convert-user] ❌ Currency conversion failed, using direct percentage:`, error);
            // Fallback to direct percentage if conversion fails
            amount = order_value * pricingRule.rate;
          }
        } else {
          // For USD, apply percentage directly
          amount = order_value * pricingRule.rate;
          console.log(`[convert-user] USD direct calc: ${order_value} × ${pricingRule.rate} = ${amount}`);
        }
        
        console.log(`[convert-user] ✅ Percentage calculation completed`);
      }
    } else {
      // Fixed pricing
      amount = pricingRule.rate;
      if (process.env.NODE_ENV === 'development') {
        console.log(`💰 [add-on-pricing] Fixed pricing: ${pricingRule.rate}`);
      }
    }

    if (process.env.NODE_ENV === 'development') {
      console.log(`💰 [add-on-pricing] Before constraints: ${amount}, min: ${min_amount}, max: ${max_amount}`);
    }

    // Apply min/max constraints - but skip for percentage services to avoid currency issues
    if (service.pricing_type !== 'percentage') {
      // Only apply min/max constraints for fixed services
      if (amount < min_amount) {
        if (process.env.NODE_ENV === 'development') {
          console.log(`💰 [add-on-pricing] FIXED service: Amount ${amount} below minimum ${min_amount}, using minimum`);
        }
        amount = min_amount;
      }
      
      if (max_amount && amount > max_amount) {
        if (process.env.NODE_ENV === 'development') {
          console.log(`💰 [add-on-pricing] FIXED service: Amount ${amount} above maximum ${max_amount}, using maximum`);
        }
        amount = max_amount;
      }
    } else {
      // For percentage services, let the natural percentage calculation stand
      if (process.env.NODE_ENV === 'development') {
        console.log(`💰 [add-on-pricing] PERCENTAGE service: Skipping min/max constraints, using calculated amount: ${amount}`);
      }
    }

    // Use quote currency conversion system for consistency
    
    console.log(`[convert-user] Service: ${service.service_key}`, {
      pricing_type: service.pricing_type,
      target_currency: currency_code,
      calculated_amount_before_conversion: amount,
      order_value_used_for_calculation: order_value,
      service_name: service.service_name,
      pricing_rule_rate: pricingRule.rate,
      is_percentage_service: service.pricing_type === 'percentage',
      is_fixed_service: service.pricing_type === 'fixed'
    });
    
    let convertedAmount = amount;
    
    if (currency_code !== 'USD') {
      if (service.pricing_type === 'percentage') {
        // PERCENTAGE SERVICES: No conversion needed!
        // The amount is already calculated from order_value in the correct currency
        // Example: ₨89,321.74 × 1.2% = ₨1,071.86 (already correct currency)
        console.log(`[convert-user] ✅ PERCENTAGE service - no conversion needed:`, {
          service_key: service.service_key,
          calculated_amount: amount,
          currency: currency_code,
          order_value: order_value,
          pricing_rate: pricingRule.rate,
          calculation_check: `${order_value} × ${pricingRule.rate} = ${order_value * pricingRule.rate}`,
          note: 'Amount should already be in target currency from percentage calculation'
        });
        convertedAmount = amount;
      } else {
        // FIXED SERVICES: Convert USD base price to target currency using quote system
        
        try {
          convertedAmount = await convertCurrency(amount, 'USD', currency_code);
          console.log(`[convert-user] ✅ FIXED service conversion: ${amount} USD → ${convertedAmount} ${currency_code}`);
        } catch (error) {
          console.error(`[convert-user] ❌ Currency conversion failed:`, error);
          convertedAmount = amount; // Fallback to original amount
        }
      }
    } else {
      console.log(`[convert-user] 💵 USD service - no conversion needed: ${amount}`);
      convertedAmount = amount;
    }

    // Use quote currency formatting system instead of custom rounding
    const finalAmount = convertedAmount; // Will be formatted by formatAmountWithFinancialPrecision in the component
    
    
    return {
      amount: finalAmount,
      min_amount,
      max_amount
    };
  }

  /**
   * Get service configuration by key or ID
   */
  private async getServiceConfig(service_key?: string | null, service_id?: string): Promise<AddonService | null> {
    let query = supabase
      .from('addon_services')
      .select('*')
      .eq('is_active', true);

    if (service_key) {
      query = query.eq('service_key', service_key);
    } else if (service_id) {
      query = query.eq('id', service_id);
    } else {
      return null;
    }

    const { data, error } = await query.single();
    
    if (error) {
      if (error.code === 'PGRST116') { // No rows found
        return null;
      }
      throw error;
    }

    return data;
  }

  /**
   * Cache pricing calculation in database for longer-term storage - DISABLED
   */
  private async cacheInDatabase(
    service_id: string,
    country_code: string,
    order_value: number,
    pricingRule: any,
    calculated_amount: number
  ) {
    // Database caching disabled - using in-memory cache only
    logger.debug('Database pricing cache disabled, using in-memory cache only');
  }

  /**
   * Get country pricing information and capabilities
   */
  async getCountryPricingInfo(country_code: string): Promise<CountryPricingInfo> {
    const startTime = Date.now();
    const operationId = `country_info_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Start performance monitoring
    performanceMonitoringService.startOperation(operationId, 'get_country_pricing_info', {
      countryCode: country_code
    });

    try {
      // Get all services with any pricing rules for this country
      const { data: services } = await supabase
        .from('addon_services')
        .select(`
          service_key,
          country_pricing_overrides!inner(country_code),
          regional_pricing(region_key, country_codes),
          continental_pricing!inner(
            continent,
            country_settings!inner(code)
          )
        `)
        .eq('is_active', true);

      // Get continent for this country via Lambda API
      const response = await lambdaDataService.getCountries();
      const countryData = response.success ? response.data.find(c => c.code === country_code) : null;

      // Process the data
      const supported_services = services?.map(s => s.service_key) || [];
      const has_country_overrides = services?.some(s => 
        s.country_pricing_overrides?.some(cpo => cpo.country_code === country_code)
      ) || false;
      
      const regional_groups = services?.flatMap(s => 
        s.regional_pricing?.filter(rp => 
          rp.country_codes?.includes(country_code)
        ).map(rp => rp.region_key) || []
      ) || [];

      const result = {
        country_code,
        continent: countryData?.continent,
        supported_services: [...new Set(supported_services)],
        regional_groups: [...new Set(regional_groups)],
        has_country_overrides
      };

      // End performance monitoring - success
      performanceMonitoringService.endOperation(
        operationId,
        'get_country_pricing_info',
        startTime,
        true,
        false,
        country_code,
        undefined,
        undefined,
        undefined,
        {
          continent: countryData?.continent,
          supportedServicesCount: result.supported_services.length,
          hasOverrides: has_country_overrides
        }
      );

      return result;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      
      // End performance monitoring - error
      performanceMonitoringService.endOperation(
        operationId,
        'get_country_pricing_info',
        startTime,
        false,
        false,
        country_code,
        undefined,
        undefined,
        errorMessage,
        { countryCode: country_code }
      );

      logger.error('Error getting country pricing info:', error);
      return {
        country_code,
        supported_services: [],
        regional_groups: [],
        has_country_overrides: false
      };
    }
  }

  /**
   * Check if a country is supported for regional pricing
   */
  async isCountrySupported(country_code: string): Promise<boolean> {
    if (!country_code || country_code.length !== 2) {
      return false;
    }

    try {
      // Check if country exists via Lambda API
      const response = await lambdaDataService.getCountries();
      if (!response.success) {
        logger.debug(`Failed to fetch countries data for ${country_code}`);
        return false;
      }

      const countryData = response.data.find(c => c.code === country_code.toUpperCase());
      return !!countryData;
    } catch (error) {
      logger.debug(`Country ${country_code} not found in countries data`);
      return false;
    }
  }

  /**
   * Clear all caches
   */
  clearCache(): void {
    this.cache.clear();
    logger.debug('[RegionalPricing] All caches cleared');
  }

  /**
   * Get item from memory cache
   */
  private getFromCache(key: string): any {
    const cached = this.cache.get(key);
    if (cached && cached.expires > Date.now()) {
      return cached.data;
    }
    if (cached) {
      this.cache.delete(key); // Remove expired entry
    }
    return null;
  }

  /**
   * Set item in memory cache
   */
  private setCache(key: string, data: any): void {
    this.cache.set(key, {
      data,
      expires: Date.now() + this.CACHE_DURATION
    });
  }


  // Removed custom rounding functions - now using formatAmountWithFinancialPrecision from quoteCurrencyUtils.ts
}

// Export singleton instance
export const regionalPricingService = RegionalPricingServiceClass.getInstance();